import { updatePreferences } from '@/analytics/consent-hub/consent-client';
import { getWacIntegrationUrl, isProduction } from '@/utils/urls.ts';

import type { FormConsentMode } from './uniform-form-schema';
import type { FormValues } from './uniform-form-validation';

const SUBMISSION_TIMEOUT_MS = 15_000;
const DEFAULT_SUBMISSION_ENDPOINT = 'wacformlead';

export type FormSubmissionEndpoint = 'wacformlead' | 'enterpriseformsubmit' | 'custom';

type ResolvedSubmissionEndpoint = {
	path: string;
	type: FormSubmissionEndpoint;
};

const FORM_SUBMISSION_PATHS: Record<Exclude<FormSubmissionEndpoint, 'custom'>, string> = {
	wacformlead: '/streamhub/wacformlead',
	enterpriseformsubmit: '/enterprise-form-submit',
};

const toSameRootPath = (endpoint?: string): string | undefined => {
	const trimmedEndpoint = endpoint?.trim();
	if (!trimmedEndpoint) return undefined;
	return `/${trimmedEndpoint.replace(/^\/+/, '')}`;
};

const getSubmissionEndpoint = (
	endpoint?: string,
	customEndpoint?: string,
): ResolvedSubmissionEndpoint => {
	if (endpoint === 'custom') {
		const customPath = toSameRootPath(customEndpoint);
		if (customPath) return { path: customPath, type: 'custom' };
	}
	if (endpoint === 'enterpriseformsubmit') {
		return { path: FORM_SUBMISSION_PATHS.enterpriseformsubmit, type: endpoint };
	}
	return { path: FORM_SUBMISSION_PATHS.wacformlead, type: DEFAULT_SUBMISSION_ENDPOINT };
};

const withTimeout = async <T>(promise: Promise<T>): Promise<T> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const timeoutPromise = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(
			() => reject(new UniformFormSubmissionError('timeout')),
			SUBMISSION_TIMEOUT_MS,
		);
	});
	try {
		return await Promise.race([promise, timeoutPromise]);
	} finally {
		if (timeoutId) clearTimeout(timeoutId);
	}
};

const isConsentHubDisabled = (): boolean =>
	!isProduction() &&
	new URLSearchParams(window.location.search).get('disableConsentHub') === 'true';

export type FormFailureCategory = 'preferences' | 'lead' | 'timeout';

export class UniformFormSubmissionError extends Error {
	public readonly category: FormFailureCategory;

	public constructor(category: FormFailureCategory) {
		super(category);
		this.name = 'UniformFormSubmissionError';
		this.category = category;
	}
}

export type SubmitUniformFormInput = {
	client: Parameters<typeof updatePreferences>[0];
	customEndpoint?: string;
	consentCheckboxValue: boolean;
	consentMode: FormConsentMode;
	displayedLegalText: string;
	formId: string;
	formType: string;
	formValues: FormValues;
	intercomAppId?: string;
	intercomTracking: boolean;
	locale: string;
	submissionEndpoint?: FormSubmissionEndpoint;
};

const getCookie = (name: string): string => {
	const match = document.cookie
		.split('; ')
		.find((item) => item.startsWith(`${name}=`))
		?.slice(name.length + 1);
	return match ? decodeURIComponent(match).replace(/"/g, '') : '';
};

const getAttribution = (): Record<string, string> => {
	const params = new URLSearchParams(window.location.search);
	return {
		utm_source: params.get('utm_source') ?? '',
		utm_medium: params.get('utm_medium') ?? '',
		utm_campaign: params.get('utm_campaign') ?? '',
		utm_content: params.get('utm_content') ?? '',
		utm_term: params.get('utm_term') ?? '',
	};
};

const getConsentTexts = (
	consentMode: FormConsentMode,
	displayedLegalText: string,
): Pick<
	Parameters<typeof updatePreferences>[1],
	'consentLabelText' | 'privacyPolicyText' | 'ndaText'
> => {
	switch (consentMode) {
		case 'generalMarketingOptIn':
			return { consentLabelText: displayedLegalText };
		case 'privacyPolicy':
			return { privacyPolicyText: displayedLegalText };
		case 'termsOfService':
		case 'eapTermsOfService':
		case 'eapVirtualAgentTermsAndConditions':
			return { ndaText: displayedLegalText };
		case 'none':
			return {};
	}
};

export const submitUniformForm = async ({
	client,
	customEndpoint,
	consentCheckboxValue,
	consentMode,
	displayedLegalText,
	formId,
	formType,
	formValues,
	intercomAppId,
	intercomTracking,
	locale,
	submissionEndpoint,
}: SubmitUniformFormInput): Promise<void> => {
	const endpoint = getSubmissionEndpoint(submissionEndpoint, customEndpoint);
	const email = String(formValues['email'] ?? '');
	const controller = new AbortController();
	const timeout = window.setTimeout(() => controller.abort(), SUBMISSION_TIMEOUT_MS);

	try {
		if (!isConsentHubDisabled()) {
			try {
				const responseStatus = await withTimeout(
					updatePreferences(client, {
						email,
						formId,
						formUrl: window.location.href,
						paramsLocale: locale,
						site: 'atlassian',
						source: 'uniform',
						consentCheckboxValue,
						...getConsentTexts(consentMode, displayedLegalText),
					}),
				);
				if (typeof responseStatus === 'number' && (responseStatus < 200 || responseStatus >= 300)) {
					throw new Error(`Consent preferences update failed with status ${responseStatus}`);
				}
			} catch (error) {
				if (error instanceof UniformFormSubmissionError) throw error;
				throw new UniformFormSubmissionError('preferences');
			}
		}

		const now = new Date();
		let response: Response;
		try {
			response = await fetch(`${getWacIntegrationUrl()}${endpoint.path}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				signal: controller.signal,
				body: JSON.stringify({
					...formValues,
					...getAttribution(),
					formType,
					...(endpoint.type === 'wacformlead' ? { formDef: 'pointALead' } : {}),
					formSubmissionId: crypto.randomUUID(),
					created_at: now.toISOString(),
					submitted: now.toString(),
					source_sequence: now.getTime(),
					locale,
					formUrl: window.location.href,
					referrer: document.referrer,
					userAgent: window.navigator.userAgent,
					marketoCookieId: getCookie('_mkto_trk'),
					anonymousId: getCookie('ajs_anonymous_id'),
					atlassianId: getCookie('ajs_user_id'),
					isIntercomTracking: intercomTracking,
					intercomAppId: intercomTracking ? (intercomAppId ?? '') : '',
				}),
			});
		} catch {
			if (controller.signal.aborted) throw new UniformFormSubmissionError('timeout');
			throw new UniformFormSubmissionError('lead');
		}
		if (!response.ok) throw new UniformFormSubmissionError('lead');
	} finally {
		window.clearTimeout(timeout);
	}
};
